'use client';

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, ArrowRight, DollarSign, Heart, TrendingUp, Shield, Headphones, Zap } from 'lucide-react';

export default function AboutPage() {
  const [particles, setParticles] = useState<Array<{ x: number; y: number }>>([]);

  useEffect(() => {
    setParticles(
      [...Array(50)].map(() => ({
        x: Math.random() * (typeof window !== 'undefined' ? window.innerWidth : 1000),
        y: Math.random() * (typeof window !== 'undefined' ? window.innerHeight : 1000)
      }))
    );
  }, []);
  const projects = [
    {
      name: 'Global Social Post',
      logo: '/assets/projects/gspblack.png',
      description: 'A social media management tool that helps users schedule and post across multiple platforms effortlessly.',
      link: 'https://globalsocialpost.com'
    },
    {
      name: 'GSP Coin',
      logo: '/assets/projects/gspcblack.png',
      description: 'A cryptocurrency designed to create a decentralized digital asset for various financial applications.',
      link: 'https://gspcoin.us'
    },
    {
      name: 'World News Guru',
      logo: '/assets/projects/wngblack.png',
      description: 'A global news platform providing breaking news, analysis, and in-depth coverage on various topics.',
      link: 'https://worldnewsguru.us'
    },
    {
      name: 'Global SMTP Mail',
      logo: '/assets/projects/gsmblack.png',
      description: 'A robust email delivery service ensuring safe and secure delivery of marketing and transactional emails.',
      link: 'https://globalsmtpmail.com'
    },
    {
      name: 'Global Intel Invest',
      logo: '/assets/projects/giiblack.png',
      description: 'An investment platform for both novice and professional traders.',
      link: 'https://globalintelinvest.com'
    },
    {
      name: 'Traffixpert',
      logo: '/assets/projects/traffixpert.png',
      description: 'A digital marketing company specializing in generating high-quality traffic for businesses.',
      link: 'https://traffixpert.com'
    },
    {
      name: 'Global Cric Info',
      logo: '/assets/projects/gciblack.png',
      description: 'A comprehensive platform for cricket lovers, providing live scores and match stats.',
      link: 'https://globalcricinfo.com'
    },
    {
      name: '1Url',
      logo: '/assets/projects/1url.png',
      description: 'A URL shortening service that simplifies sharing long URLs and enhances tracking capabilities.',
      link: 'https://1url.my'
    }
  ];

  const values = [
    {
      title: 'Affordable, High-Quality Hosting',
      description: 'Quality hosting accessible to everyone with competitive pricing.'
    },
    {
      title: 'Reliability',
      description: '99.99% uptime and fast load times for your website or application.'
    },
    {
      title: 'Scalability',
      description: 'Scalable hosting solutions that grow with your business.'
    },
    {
      title: 'Security',
      description: 'Top-tier security features including DDoS protection and SSL encryption.'
    },
    {
      title: 'Customer-Centric Support',
      description: '24/7 customer service focused on solving problems efficiently.'
    },
    {
      title: 'Innovation & Technology',
      description: 'Cutting-edge infrastructure and continuous technological advancement to provide the best hosting experience.'
    }
  ];

  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden -mt-24">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"></div>
        <div className="absolute inset-0 bg-[url('/assets/grid-pattern.svg')] bg-[length:40px_40px] opacity-[0.04]"></div>
        <div className="absolute inset-0 bg-[url('/assets/noise.svg')] opacity-[0.02] mix-blend-overlay"></div>
        
        {/* Animated glowing orbs */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -left-40 w-96 h-96 bg-gradient-to-br from-primary-500/30 to-blue-500/30 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-gradient-to-br from-purple-500/30 to-pink-500/30 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gradient-to-br from-blue-500/20 via-purple-500/20 to-pink-500/20 rounded-full blur-3xl animate-pulse delay-2000"></div>
        </div>

        {/* Binary data particles */}
        <div className="absolute inset-0 overflow-hidden">
          {particles.map((particle: { x: number; y: number }, i: number) => (
            <motion.div
              key={i}
              className="absolute text-white/10 font-mono text-sm"
              initial={{
                x: particle.x,
                y: particle.y,
                opacity: 0
              }}
              animate={{
                y: [null, -100],
                opacity: [0, 1, 0],
                x: [null, particle.x + (Math.random() * 100 - 50)]
              }}
              transition={{
                duration: Math.random() * 10 + 10,
                repeat: Infinity,
                delay: Math.random() * 5,
                ease: "linear"
              }}
            >
              {Math.random() > 0.5 ? '1' : '0'}
            </motion.div>
          ))}
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative mt-20">
          <div className="text-center">
            <motion.h1 
              className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              About <span className="text-transparent bg-clip-text bg-gradient-rainbow">1 Dollar Cloud</span>
            </motion.h1>
            <motion.p 
              className="text-xl text-slate-300 max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              Affordable hosting without compromising on quality – flexible hosting plans and competitive pricing
            </motion.p>
          </div>
        </div>
      </section>

      {/* Welcome Section */}
      <section className="py-20 relative overflow-hidden bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative h-[500px] rounded-xl overflow-hidden bg-gradient-to-br from-slate-800 to-slate-900"
            >
              {/* Value-Focused Cloud Visualization */}
              <div className="absolute inset-0">
                {/* Background Elements */}
                <div className="absolute inset-0 bg-[url('/assets/grid-pattern.svg')] bg-[length:30px_30px] opacity-[0.03]"></div>
                
                {/* Dollar Value Indicators */}
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="relative w-[400px] h-[400px]">
                    {/* Value Circle */}
                    <div className="absolute inset-0 border-4 border-primary-500/20 rounded-full"></div>
                    
                    {/* Dollar Signs */}
                    {[...Array(8)].map((_, i) => (
                      <motion.div
                        key={i}
                        className="absolute text-primary-500/30 text-4xl font-bold"
                        style={{
                          left: `${50 + Math.cos(i * Math.PI / 4) * 45}%`,
                          top: `${50 + Math.sin(i * Math.PI / 4) * 45}%`,
                        }}
                        animate={{
                          opacity: [0.3, 0.8, 0.3],
                          scale: [1, 1.2, 1],
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          delay: i * 0.3,
                        }}
                      >
                        $
                      </motion.div>
                    ))}
                    
                    {/* Central Value Proposition */}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <motion.div
                          className="text-6xl font-bold text-primary-500 mb-2"
                          animate={{
                            scale: [1, 1.1, 1],
                          }}
                          transition={{
                            duration: 2,
                            repeat: Infinity,
                          }}
                        >
                          $1
                        </motion.div>
                        <div className="text-xl text-slate-400">per month</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Cloud Features */}
                {[
                  { text: "High Performance", top: "20%", left: "10%" },
                  { text: "99.99% Uptime", top: "20%", right: "10%" },
                  { text: "24/7 Support", bottom: "20%", left: "10%" },
                  { text: "SSL Security", bottom: "20%", right: "10%" },
                ].map((feature, i) => (
                  <motion.div
                    key={i}
                    className="absolute bg-slate-800/50 rounded-lg px-4 py-2 text-sm text-slate-300"
                    style={{
                      top: feature.top,
                      bottom: feature.bottom,
                      left: feature.left,
                      right: feature.right,
                    }}
                    animate={{
                      y: [0, -5, 0],
                      opacity: [0.7, 1, 0.7],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      delay: i * 0.5,
                    }}
                  >
                    {feature.text}
                  </motion.div>
                ))}

                {/* Connection Lines */}
                <div className="absolute inset-0">
                  {[...Array(4)].map((_, i) => (
                    <motion.div
                      key={i}
                      className="absolute h-[2px] bg-gradient-to-r from-primary-500/0 via-primary-500/20 to-primary-500/0"
                      style={{
                        width: '150px',
                        left: '50%',
                        top: '50%',
                        transform: `rotate(${i * 90}deg)`,
                      }}
                      animate={{
                        opacity: [0, 0.5, 0],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        delay: i * 0.5,
                      }}
                    />
                  ))}
                </div>
              </div>

              {/* Subtle Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 via-slate-900/20 to-transparent"></div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold text-white mb-6">
                Welcome to <span className="text-transparent bg-clip-text bg-gradient-rainbow">1 Dollar Cloud</span>
              </h2>
              <p className="text-slate-300 mb-6">
                As part of Global Ads Media, LLC, we are dedicated to providing high-performance, scalable hosting solutions at an unbeatable price. Whether you're launching a personal blog, scaling an e-commerce business, or running a large corporate website, 1 Dollar Cloud is here to ensure your website performs at its best, while keeping costs low.
              </p>
              <p className="text-slate-300">
                Our cloud-based infrastructure ensures that your website can scale quickly and efficiently, without any downtime or interruptions.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Global Ads Media Section */}
      <section className="py-20 relative overflow-hidden bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="bg-white p-8 rounded-xl h-[300px] flex items-center justify-center"
            >
              <img
                src="/assets/projects/gamblack.png"
                alt="Global Ads Media"
                width={400}
                height={200}
                className="object-contain"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl font-bold text-white mb-6">
                Part of <span className="text-transparent bg-clip-text bg-gradient-rainbow">Global Ads Media</span>
              </h2>
              <p className="text-slate-300 mb-6">
                1 Dollar Cloud is a subsidiary of Global Ads Media, LLC, a leading digital solutions company with a diverse range of successful projects spanning several industries. Since our inception, Global Ads Media has focused on creating innovative, customer-centric platforms that drive business success.
              </p>
              <p className="text-slate-300">
                With a forward-thinking approach, Global Ads Media is committed to empowering businesses by providing them with tools and services that help them thrive in the digital world.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section className="py-20 relative overflow-hidden bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl font-bold text-white mb-6">
              Our <span className="text-transparent bg-clip-text bg-gradient-rainbow">Projects</span>
            </h2>
            <p className="text-slate-300 max-w-3xl mx-auto">
              Explore our diverse range of successful projects spanning several industries.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {projects.map((project, index) => (
              <motion.div
                key={project!.name}
                className="bg-slate-900/50 rounded-xl p-6 hover:bg-slate-900/80 transition-all duration-300 flex flex-col"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="mb-4 relative h-40 bg-white rounded-lg p-4 flex items-center justify-center">
                  <img
                    src={project.logo ?? '/assets/default-project.png'}
                    alt={`${project.name} logo`}
                    width={200}
                    height={120}
                    className="rounded-lg"
                  />
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{project?.name ?? 'Unnamed Project'}</h3>
                <p className="text-slate-400 mb-4 flex-grow">{project?.description ?? 'No description available'}</p>
                <Link
                  to={project?.link ?? '#'}
                  className="inline-flex items-center text-primary-400 hover:text-primary-300 mt-auto"
                  target="_blank"
                >
                  Visit Website <ArrowRight className="ml-2" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Founders Section */}
      <section className="py-20 relative overflow-hidden bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl font-bold text-white mb-6">
              Meet the <span className="text-transparent bg-clip-text bg-gradient-rainbow">Founders</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <motion.div
              className="bg-slate-900/50 rounded-xl p-8 flex flex-col"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="flex items-start mb-4">
                <div>
                  <h3 className="text-2xl font-bold text-white">Dinesh Agrawal</h3>
                  <p className="text-primary-400 text-lg">Founder</p>
                </div>
              </div>
              <p className="text-slate-300 mb-6 flex-grow">
                As the driving force behind Global Ads Media, Dinesh Agrawal brings years of experience and expertise in the tech and digital marketing industries. He has led the development of multiple successful projects, including 1 Dollar Cloud, and has a passion for creating user-centric platforms.
              </p>
              <a
                href="https://www.linkedin.com/in/dinesh-agrawal-gam/"
                className="inline-flex items-center text-primary-400 hover:text-primary-300 mt-auto"
                target="_blank"
                rel="noopener noreferrer"
              >
                View LinkedIn Profile <ArrowRight className="ml-2" />
              </a>
            </motion.div>

            <motion.div
              className="bg-slate-900/50 rounded-xl p-8 flex flex-col"
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="flex items-start mb-4">
                <div>
                  <h3 className="text-2xl font-bold text-white">Daniel Coy</h3>
                  <p className="text-primary-400 text-lg">Co-Founder</p>
                </div>
              </div>
              <p className="text-slate-300 mb-6 flex-grow">
                A strategic thinker and operational expert, Daniel has played a key role in shaping the vision and growth of Global Ads Media. His focus on customer satisfaction and efficient business operations has been pivotal in the success of all the company's ventures.
              </p>
              <a
                href="https://www.linkedin.com/in/daniel-coy-gam/"
                className="inline-flex items-center text-primary-400 hover:text-primary-300 mt-auto"
                target="_blank"
                rel="noopener noreferrer"
              >
                View LinkedIn Profile <ArrowRight className="ml-2" />
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 relative overflow-hidden bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl font-bold text-white mb-6">
              Our Mission and <span className="text-transparent bg-clip-text bg-gradient-rainbow">Values</span>
            </h2>
            <p className="text-slate-300 max-w-3xl mx-auto">
              We believe in offering more than just affordable hosting services—we aim to empower businesses and individuals with the tools they need to thrive online.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                className="bg-slate-900/50 rounded-xl p-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 rounded-full bg-gradient-rainbow flex items-center justify-center">
                    {value.title === 'Affordable, High-Quality Hosting' ? <DollarSign className="text-white text-xl" />
                      : value.title === 'Reliability' ? <Heart className="text-white text-xl" />
                        : value.title === 'Scalability' ? <TrendingUp className="text-white text-xl" />
                          : value.title === 'Security' ? <Shield className="text-white text-xl" />
                            : value.title === 'Customer-Centric Support' ? <Headphones className="text-white text-xl" />
                              : value.title === 'Innovation & Technology' ? <Zap className="text-white text-xl" />
                                : <Check className="text-white text-xl" />}
                  </div>
                </div>
                <h3 className="text-xl font-semibold text-white mb-2">{value.title}</h3>
                <p className="text-slate-400">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}